import { useEffect } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectStepContext,
  selectStepContextLoading,
  selectStepContextError,
} from '../../services/documentUpload.slice'
import { fetchDocumentContexts } from '../../services/documentActions'
import { EndOfYearAdminReviewStep } from '../../end-of-year-review/types'

/**
 * Hook for fetching and managing document step context from Redux.
 * It fetches step context data, while the existing `useDocuments` hook
 * transforms that data into utility functions. They work together:
 *
 * This hook replaces the manual pattern of:
 * ```tsx
 * const [payrollContext, setPayrollContext] = useState(null)
 *
 * useEffect(() => {
 *   const fetchData = async () => {
 *     const step = await userGetBookkeepingStep('docs-payroll')(dispatch)
 *     setPayrollContext(step?.context)
 *   }
 *   fetchData()
 * }, [dispatch])
 * ```
 *
 * @param stepId - Step identifier (e.g., EndOfYearAdminReviewStep.docsPayroll)
 * @returns Object with data, loading, and error states
 *
 * @example
 * ```tsx
 * function DocumentReviewContainer() {
 *   const { data: payrollContext, loading, error } = useStepContext(
 *     EndOfYearAdminReviewStep.docsPayroll
 *   )
 *
 *   if (loading) return <Spinner />
 *   if (error) return <ErrorMessage message={error} />
 *
 *   return <PayrollDocumentComponent stepContext={payrollContext} />
 * }
 * ```
 */
export function useStepContext(stepId: EndOfYearAdminReviewStep) {
  const dispatch = useAppDispatch()
  const stepContext = useReselector(selectStepContext, stepId)
  const error = useReselector(selectStepContextError, stepId)
  const loading = useReselector(selectStepContextLoading, stepId)

  // Fetch all document contexts on mount if not already loaded
  useEffect(() => {
    if (!stepContext && !loading && !error) {
      dispatch(fetchDocumentContexts())
    }
  }, [dispatch, stepContext, loading, error])

  return {
    data: stepContext,
    loading,
    error,
  }
}
